import React, { useEffect, useState, useContext, useCallback } from 'react';
import * as queryString from 'query-string';
import { PageHeader, Button, Skeleton, Pagination, Row, Col } from 'antd';
import { Link, useRouteMatch, RouteComponentProps } from 'react-router-dom';
import AuthContext from '@packages/contexts/auth/authContext';
import { Permission } from '../../types/Auth';
import { FORM_MODE } from '../../types/Form';
import { Client } from '../../types/Client';
import { ClientList, ClientListSearchFilters } from '../../components/Client';
import { Can } from '../../components/Shared';
import ClientController from '../../controllers/client.controller';

const ClientListContainer = ({
    history,
    match: { url },
    location: { search },
}: RouteComponentProps): React.ReactElement => {
    const { user } = useContext(AuthContext);

    const [componentStatusFlags, setComponentStatusFlags] = useState<{ isLoadingData: boolean }>({
        isLoadingData: true,
    });

    const routeMatch = useRouteMatch();
    const [clients, setClients] = useState<Client[]>([]);
    const [clientsCount, setClientsCount] = useState<number>(0);
    const [clientOffset, setClientOffset] = useState<number>(0);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

    const [appliedFilters, setAppliedFilters] = useState<any>(queryString.parse(search, { arrayFormat: 'bracket' }));

    const getClients = async (offset: number, filters?: { [key: string]: any }): Promise<void> => {
        try {
            setIsLoadingData(true);
            const {
                list: clientList,
                count: clientsCnt,
            }: { list: Client[]; count: number } = await ClientController.getAllClients(offset, undefined, filters);
            setClientsCount(clientsCnt);
            setClients(clientList.map((client: Client, index: number) => ({ key: index, ...client })));
        } finally {
            setIsLoadingData(false);
        }
    };

    const setFiltersToApply = (filters: any, opts?: { isPaging: boolean }): void => {
        setAppliedFilters((prevAppliedFilters: any) => {
            const filterResult = opts && opts.isPaging ? { ...prevAppliedFilters, ...filters } : { ...filters };
            const query = queryString.stringify(filterResult, { arrayFormat: 'bracket' });
            history.push(`${url}?${query}`);
            return filterResult;
        });
    };

    const handleDeleteClient = useCallback(
        async (clientId: number): Promise<void> => {
            try {
                await ClientController.deleteClientById(clientId);
            } finally {
                getClients(clientOffset);
            }
        },
        [clientOffset],
    );

    useEffect(() => {
        getClients(clientOffset, appliedFilters);
    }, [clientOffset, appliedFilters]);

    return (
        <div className="shadow-sm">
            <PageHeader
                className="site-page-header py-2 justify-content-end"
                title={<p className="h5 m-0">Clients</p>}
                extra={[
                    <Button
                        key="refresh-client-list"
                        name="btn-refresh-client-list"
                        id="btn-refresh-client-list"
                        title="Refresh list"
                        className="d-inline-flex align-items-center"
                        onClick={(): Promise<void> => getClients(clientOffset)}
                    >
                        Refresh list
                    </Button>,
                    <Can
                        key="add-client-list-validator"
                        role={user!.role}
                        perform={Permission.CLIENT_ADD}
                        yes={(): React.ReactElement => (
                            <Link
                                key="add-client"
                                id="btn-add-client"
                                title="Add client"
                                to={`${routeMatch.path}/${FORM_MODE.ADD}`}
                                className="ant-btn text-white btn-primary bg-primary"
                            >
                                Add client
                            </Link>
                        )}
                        no={(): React.ReactElement => <span />}
                    />,
                ]}
            />
            {isLoadingData ? (
                <Skeleton active className="p-4" />
            ) : (
                <>
                    <ClientListSearchFilters
                        onSetSearchFilters={setFiltersToApply}
                        initialFilters={appliedFilters}
                        clients={clients}
                    />
                    <ClientList
                        totalClients={clientsCount}
                        clients={clients}
                        onDeleteClient={handleDeleteClient}
                        rbacRules={{ userRole: user!.role, permissions: Permission }}
                    />
                    <Row justify="space-around" style={{ padding: '10px' }}>
                        <Col>
                            <Pagination
                                defaultCurrent={1}
                                current={Math.round(clientOffset / 100) + 1}
                                pageSize={100}
                                total={clientsCount}
                                showSizeChanger={false}
                                onChange={(page: number): void => setClientOffset((page - 1) * 100)}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default ClientListContainer;
